import { useCallback, useEffect, type KeyboardEvent } from 'react'
import { useStudioStore } from '@/store/studio-store'
import type { RailItemId } from '../types'
import { useStudioLocale } from '@/i18n'
import { getStudioAuthorityTaskCopy } from '@/i18n/studio-authority-copy'
import type { StudioDictionary } from '@/i18n/studio-dictionaries'
import { inferStudioProjectOutputKind, type StudioProjectOutputKind } from '@/features/project/lib/studio-project-plan'
import {
  findStudioAuthorityTask,
  findStudioAuthorityTaskByRailItem,
  getDefaultStudioRenewalAuthorityView,
  readStudioAuthorityDeepLink,
  resolveStudioAuthorityDomain,
  type StudioAuthorityDomain,
} from '../studio-authority-workspaces'
import { activateStudioAuthorityTask } from '../studio-authority-activation'
import { getCoogoAsIsArtifactLabel, getCoogoInsightsArtifactLabel } from '../coogo-ide-renewal-copy'
import { COOGO_IDE_CHROME_COPY } from '../coogo-ide-chrome-copy'
import {
  readAsIsSnapshotFromSettings,
  resolveArtifactModeTabs,
} from '@/features/preview/services/as-is-snapshot'
import { useAsIsSourceMarkupPresent } from '@/features/preview/hooks/use-as-is-source-markup'
import { CockpitTabSignal } from "../cockpit/cockpit-tab-signal";
import {
  selectCockpitAttentionRoute,
  type StudioCockpitTab,
  type StudioCockpitTabState,
} from "../cockpit/studio-cockpit-activity";
import { useStudioCockpit } from "../cockpit/use-studio-cockpit";

// As-Is is a read-only view of the captured original — it can never have
// cockpit-tracked work in flight, so it always reads as idle rather than
// borrowing another lane's badge (Fix round 1, related symptom: it used to
// render the "app" lane's badge via toCockpitTab's "as-is" -> "app" mapping).
const AS_IS_IDLE_COCKPIT_TAB_STATE: StudioCockpitTabState = {
  kind: "idle",
  unreadCount: 0,
  activities: [],
};

export const STUDIO_ARTIFACT_MODES = [
  "primary",
  "as-is",
  "design",
  "guide",
  "insights",
] as const;

export type StudioArtifactMode = (typeof STUDIO_ARTIFACT_MODES)[number];

// All 5 modes, in display order. "as-is" sits directly after "primary" so
// the original and the to-be are adjacent for comparison (matches
// `resolveArtifactModeTabs`'s ordering — as-is-snapshot.ts). Whether "as-is"
// actually renders as a tab is NOT decided here: `visibleOptions` below
// filters this list through `resolveArtifactModeTabs(projectSettings)` on
// every render, so a project without a snapshot never offers it.
const OPTIONS: Array<{
  id: StudioArtifactMode;
  labelKey?: keyof StudioDictionary["artifacts"];
}> = [
  { id: "primary" },
  { id: "as-is" },
  { id: "design", labelKey: "design" },
  { id: "guide", labelKey: "guide" },
  { id: "insights", labelKey: "insights" },
];

/**
 * StudioCockpitTab ("app" | "design" | "guide" | "status") predates the
 * As-Is mode and has no attention-tracking lane for it (the cockpit only
 * watches primary/design/guide/insights work).
 *
 * The "as-is" case below exists ONLY so this stays exhaustive over
 * StudioArtifactMode (never an implicit/`any` fallthrough) — it is never
 * actually consulted for "as-is": `activateOption` bypasses the whole
 * attention-routing check for it (Fix round 1, Critical — routing "as-is"
 * through the "app" lane there let an unreviewed app-lane activity silently
 * redirect an As-Is click to the primary domain), and the render loop below
 * gives "as-is" a fixed idle badge state instead of consulting this mapping
 * (same fix round, related symptom — it must not wear the app lane's
 * badge). Do not start relying on this return value for "as-is" without
 * re-checking both of those call sites.
 */
function toCockpitTab(mode: StudioArtifactMode): StudioCockpitTab {
  switch (mode) {
    case "primary":
      return "app";
    case "insights":
      return "status";
    case "design":
      return "design";
    case "guide":
      return "guide";
    case "as-is":
      return "app";
  }
}

function readApplicationKind(value: unknown): string | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const applicationKind = (value as Record<string, unknown>).applicationKind;
  return typeof applicationKind === "string" ? applicationKind : null;
}

export function resolveStudioArtifactMode(
  railItem: RailItemId,
): StudioArtifactMode | null {
  return resolveStudioAuthorityDomain(railItem);
}

export function useStudioProjectOutputKind(): StudioProjectOutputKind {
  const currentProject = useStudioStore((s) => s.currentProject);
  const currentProjectName = useStudioStore((s) => s.currentProjectName);
  const projectMeta = useStudioStore((s) => s.projectMeta);
  const projectPlan = useStudioStore((s) => s.projectPlan);
  return (
    projectPlan?.outputKind ??
    inferStudioProjectOutputKind(
      [
        projectPlan?.intent,
        projectMeta?.name,
        projectMeta?.description,
        readApplicationKind(projectMeta?.metadata),
        currentProjectName,
        currentProject?.manifest?.name,
        readApplicationKind(currentProject?.manifest),
      ]
        .filter(Boolean)
        .join(" "),
    )
  );
}

/**
 * fix-round-3: this hook is what five UI surfaces (BottomBar, ChatHeader,
 * ConversationTargetSummary, AgentCollaborationSummary, ContextBadge) go
 * through, so making it source-markup-aware fixed all of them without
 * patching five separate call sites — and without a caller being able to
 * forget to pass it, unlike a parameter every call site must remember.
 *
 * fix-round-4: the source-truth-vs-derived answer itself now comes from
 * `useAsIsSourceMarkupPresent` (features/preview/hooks/use-as-is-source-markup.ts),
 * the single decision point every React surface shares — this hook, the tab
 * below, the chat rail, the chat composer's placeholder, and the chunk-wait
 * line. Do not re-read `projectSettings` and call `hasAsIsSourceMarkup`
 * again here; a second read is how rounds 2 and 3 drifted apart.
 */
export function useStudioArtifactLabel(railItem: RailItemId): string {
  const { locale, strings } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const outputKind = useStudioProjectOutputKind()
  const authorityDomain = useStudioStore((s) => s.chatContext?.authorityDomain)
  const asIsSourceMarkupPresent = useAsIsSourceMarkupPresent()
  const mode = authorityDomain ?? resolveStudioArtifactMode(railItem)
  if (mode === 'primary') return strings.artifacts[outputKind]
  if (mode === 'as-is') return getCoogoAsIsArtifactLabel(locale, asIsSourceMarkupPresent)
  if (mode === 'design') return strings.artifacts.design
  if (mode === 'guide') return strings.artifacts.guide
  if (mode === 'insights') return getCoogoInsightsArtifactLabel(locale)
  return copy.workspace
}

export function useStudioConversationSurfaceLabel(railItem: RailItemId): string {
  const artifactLabel = useStudioArtifactLabel(railItem)
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const outputKind = useStudioProjectOutputKind()
  const context = useStudioStore((s) => s.chatContext)
  const asIsSourceMarkupPresent = useAsIsSourceMarkupPresent()
  const domain = context?.authorityDomain ?? resolveStudioAuthorityDomain(railItem)
  if (domain) {
    const task = context?.authorityDomain
      ? findStudioAuthorityTask(
      domain,
      context.authorityView,
      outputKind)
      : findStudioAuthorityTaskByRailItem(domain, railItem, outputKind)
    const taskCopy = getStudioAuthorityTaskCopy(
      locale,
      domain,
      outputKind,
      task,
      asIsSourceMarkupPresent,
    );
    return `${artifactLabel} · ${taskCopy.label}`;
  }
  return copy.surfaceLabels[railItem] ?? artifactLabel
}

export function ArtifactModeSwitcher({ compact = false }: { compact?: boolean }) {
  const { locale, strings } = useStudioLocale()
  const activeRailItem = useStudioStore((s) => s.activeRailItem)
  const chatContext = useStudioStore((s) => s.chatContext)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const setWorkingPageView = useStudioStore((s) => s.setWorkingPageView)
  const { states: cockpitStates } = useStudioCockpit()
  const outputKind = useStudioProjectOutputKind()
  const activeMode = chatContext?.authorityDomain ?? resolveStudioArtifactMode(activeRailItem)
  // `settings` is a SIBLING of the loaded app, not a field on it —
  // `toLoadedApp` never emits one and `LoadedApp` declares none, so
  // `currentProject.settings` was permanently `undefined` and this gate could
  // never open (FINAL-REVIEW C1). `openServerProject` parks the row on
  // `projectSettings`, alongside `projectMeta` / `projectPlan`.
  const projectSettings = useStudioStore((s) => s.projectSettings)
  // fix-round-4 — same single decision point the label hooks above use.
  const asIsSourceMarkupPresent = useAsIsSourceMarkupPresent()
  // The As-Is tab is offered only for a conversion project (a valid
  // `settings.asIsSnapshot`) — resolveArtifactModeTabs is the single source
  // of truth for that gate (as-is-snapshot.ts), so a greenfield project
  // never shows an empty As-Is tab.
  const visibleModeIds = resolveArtifactModeTabs(projectSettings)
  const visibleOptions = OPTIONS.filter((option) =>
    visibleModeIds.includes(option.id),
  )
  const activateWorkspace = useCallback(
    (domain: StudioAuthorityDomain, requestedView?: string) => {
      activateStudioAuthorityTask({
        domain,
        outputKind,
        requestedView: requestedView ?? getDefaultStudioRenewalAuthorityView(domain, outputKind),
      })
    },
    [outputKind],
  );
  const activateOption = useCallback(
    (option: (typeof OPTIONS)[number]) => {
      // As-Is must open its own view unconditionally. It shares the "app"
      // cockpit lane (toCockpitTab) only for the badge mapping's type
      // exhaustiveness, not for real routing — consulting that lane's
      // attention here (Fix round 1, Critical) let an unreviewed app-lane
      // activity (e.g. right after generating the to-be) silently redirect
      // an As-Is click to the primary domain instead. Every other tab's
      // hijack destination stays in the domain it represents, so this
      // bypass is scoped to "as-is" only — the mechanism itself is kept for
      // primary/design/guide/insights.
      if (option.id === "as-is") {
        activateWorkspace(option.id);
        return;
      }
      const cockpitTab = toCockpitTab(option.id);
      const attention = selectCockpitAttentionRoute(
        cockpitStates[cockpitTab],
      );
      if (attention) {
        activateStudioAuthorityTask({
          domain: attention.destination.domain,
          requestedView: attention.destination.authorityView,
          railItem: attention.destination.railItem,
          selection: attention.destination.selection,
          targetRef: attention.destination.targetRef,
          outputKind,
          attention,
        });
        return;
      }
      activateWorkspace(option.id);
    },
    [activateWorkspace, cockpitStates, outputKind],
  );

  useEffect(() => {
    const desiredWorkingView = outputKind === "document" ? "report" : "app";
    if (useStudioStore.getState().workingPageView !== desiredWorkingView) {
      setWorkingPageView(desiredWorkingView);
    }
    const deepLink = readStudioAuthorityDeepLink(outputKind);
    if (deepLink) {
      // An "as-is" deep link (written by updateStudioAuthorityDeepLink
      // every time As-Is is opened) outlives the snapshot it pointed to —
      // reload after the snapshot is lost, or opening the same link
      // against a different project, would otherwise activate a domain
      // with no tab and no content (ActiveSurface's defensive `null`),
      // stranding the user on a blank pane with nothing to click out of
      // it. Refuse it and land on the default domain instead (Fix round 1,
      // Important) — same check `visibleModeIds`/`resolveArtifactModeTabs`
      // above already uses to decide whether the tab itself is offered.
      const deepLinkIsUnsatisfiableAsIs =
        deepLink.domain === "as-is" &&
        !readAsIsSnapshotFromSettings(projectSettings);
      if (!deepLinkIsUnsatisfiableAsIs) {
        activateWorkspace(deepLink.domain, deepLink.task.id);
        return;
      }
      activateWorkspace("primary");
      return;
    }
    const currentContext = useStudioStore.getState().chatContext;
    if (!currentContext?.authorityDomain) {
      activateWorkspace("primary");
    }
  }, [
    activateWorkspace,
    currentProjectId,
    outputKind,
    projectSettings,
    setWorkingPageView,
  ]);

  const handleArrowNavigation = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const focusedDomain = event.currentTarget.dataset.authorityDomain as
      | StudioArtifactMode
      | undefined;
    // Cycle only through the tabs actually on screen — an "as-is" entry
    // hidden by resolveArtifactModeTabs must never become a keyboard-nav
    // target (there is no button in the DOM to focus).
    const currentIndex = visibleOptions.findIndex(
      (option) => option.id === (focusedDomain ?? activeMode),
    );
    const nextIndex =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? visibleOptions.length - 1
          : (currentIndex +
              (event.key === "ArrowRight" ? 1 : -1) +
              visibleOptions.length) %
            visibleOptions.length;
    const next = visibleOptions[nextIndex];
    activateOption(next);
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLButtonElement>(
          `[data-authority-domain="${next.id}"]`,
        )
        ?.focus();
    });
  };

  return (
    <div
      className={`flex min-w-0 max-w-full items-center rounded-[10px] bg-muted p-px ${
        compact ? "overflow-x-auto [scrollbar-width:none]" : "lg:p-[3px]"
      }`}
      aria-label={strings.artifacts.label}
      role="tablist"
      data-testid="artifact-mode-switcher"
      data-compact={compact ? "true" : "false"}
    >
      {visibleOptions.map((option) => {
        const active = option.id === activeMode
        const labelKey = option.labelKey ?? outputKind
        const label =
          option.id === 'insights'
            ? getCoogoInsightsArtifactLabel(locale)
            : option.id === 'as-is'
              // fix-round-2: the tab must say "Original" (not "Original
              // structure") when sourceMarkup is present — the SAME
              // predicate the pane and the chat rail derive from, so all
              // three can never disagree about the same snapshot again.
              ? getCoogoAsIsArtifactLabel(locale, asIsSourceMarkupPresent)
              : strings.artifacts[labelKey]
        const accessibleLabel = label
        const cockpitTab = toCockpitTab(option.id);
        // As-Is is read-only and can never have work in flight — it must
        // not wear the "app" lane's badge (Fix round 1, related symptom).
        const cockpitTabState =
          option.id === "as-is"
            ? AS_IS_IDLE_COCKPIT_TAB_STATE
            : cockpitStates[cockpitTab]
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            aria-label={accessibleLabel}
            title={accessibleLabel}
            data-authority-domain={option.id}
            data-active={active ? 'true' : 'false'}
            onKeyDown={handleArrowNavigation}
            onClick={() => {
              activateOption(option);
            }}
            className={`${
              compact
                ? "h-11 min-w-11 px-2 text-[11px]"
                : "h-11 min-w-14 px-3 text-[11px] lg:h-7 lg:min-w-20 lg:px-5 lg:text-[12.5px]"
            } touch-manipulation overflow-hidden rounded-lg font-medium transition-colors ${
              active
                ? 'bg-background font-semibold text-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
            }`}
          >
            <span className="flex min-w-0 items-center justify-center gap-1">
              <span
                data-active-marker
                className={`hidden h-1.5 w-1.5 shrink-0 rounded-full sm:block ${
                  active ? "bg-primary" : "bg-transparent"
                }`}
                aria-hidden
              />
              <span className="truncate">{label}</span>
              <CockpitTabSignal
                tab={cockpitTab}
                label={label}
                selected={active}
                state={cockpitTabState}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
}
